import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { CONFIG_URLS_API } from '../../../environments/config-envs';
import { environment } from '../../../environments/environment';
import { LoginModel } from '../../model/login.model';
import { LocalStorageService } from '../../services/localstorage.service';
import { SnackBarService } from '../../services/snack-bar-service.service';
import {
  AdmRedePosto,
  ApiResponse,
  GrupoRede,
  GrupoRedeFilter,
  LogMasterPosto,
  PerfilMaster,
  PostosMaster,
  UsuarioMaster,
} from '../models';
import { FileNameGenerator } from '../utils';

/**
 * Service for managing Admin Network Posts operations
 */
@Injectable({
  providedIn: 'root',
})
export class AdmRedePostosService {
  private readonly http = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly snack = inject(SnackBarService);

  private readonly _usuario$ = new BehaviorSubject<LoginModel | null>(null);
  public readonly usuario$ = this._usuario$.asObservable();

  get usuario(): LoginModel | null {
    return this._usuario$.value;
  }

  constructor() {
    this.initializeUsuario();
  }

  private initializeUsuario(): void {
    const userData = this.localStorageService.getItem('userData');
    if (userData) {
      try {
        this._usuario$.next(JSON.parse(userData));
      } catch {
        this._usuario$.next(null);
      }
    }
  }

  private getHeaders(): { headers: HttpHeaders } {
    const guid = this.usuario?.retorno?.guid || '';
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        userGuid: guid,
      }),
    };
  }

  private buildUrl(endpoint: string): string {
    return `${environment.host_ctf_service}${endpoint}`;
  }

  private showError(messages: string[]): void {
    let mensagensConcatenadas = messages.join(' ');
    this.snack.flashMessage(
      mensagensConcatenadas || 'Erro na requisição ao carregar as redes',
      ['snackbar-error'],
      'Fechar',
      3000
    );
  }

  private handleHttpError<T>(
    error: HttpErrorResponse,
    defaultMessage: string
  ): Observable<ApiResponse<T>> {
    let errorMessages: string[] = [];

    if (
      error.error &&
      error.error.mensagens &&
      Array.isArray(error.error.mensagens)
    ) {
      errorMessages = error.error.mensagens;
    } else if (error.error && error.error.message) {
      errorMessages = [error.error.message];
    } else if (error.message) {
      errorMessages = [error.message];
    } else {
      errorMessages = [
        `${defaultMessage} (Status: ${error.status} ${error.statusText || ''})`,
      ];
    }

    this.showError(errorMessages);
    return of({
      sucesso: false,
      retorno: null,
      mensagens: errorMessages,
    } as ApiResponse<T>);
  }

  // ==================== Grupo Rede ====================

  buscarListaGrupoRede(filtro: GrupoRedeFilter): Observable<GrupoRede[]> {
    const url = this.buildUrl(CONFIG_URLS_API.API_BuscarGrupoRede);
    return this.http
      .post<ApiResponse<GrupoRede[]>>(url, filtro.toJson(), this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<GrupoRede[]>(
            error,
            'Erro ao buscar grupos de rede'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao buscar grupos de rede']
              );
            }
            return [];
          }
          return (response.retorno || []).map((item) =>
            GrupoRede.fromJson(item)
          );
        })
      );
  }

  // ==================== Adm Rede Posto ====================

  buscarListaAdmRedePosto(): Observable<AdmRedePosto[]> {
    const url = this.buildUrl(CONFIG_URLS_API.API_BuscarListaMaster);
    return this.http
      .get<ApiResponse<AdmRedePosto[]>>(url, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<AdmRedePosto[]>(
            error,
            'Erro ao carregar as redes'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao carregar as redes']
              );
            }
            return [];
          }
          return (response.retorno || []).map((item: any) =>
            AdmRedePosto.fromJson(item)
          );
        })
      );
  }

  criarAdmDeRede(novaMaster: AdmRedePosto): Observable<ApiResponse<any>> {
    const url = this.buildUrl(CONFIG_URLS_API.API_CriarAdmRedePosto);
    return this.http
      .post<ApiResponse<any>>(url, novaMaster.toJson(), this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<ApiResponse<any>>(error, 'Erro ao criar a rede')
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(response.mensagens || ['Erro ao criar a rede']);
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  editarAdmRedePost(masterEditada: AdmRedePosto): Observable<ApiResponse<any>> {
    const url = this.buildUrl(CONFIG_URLS_API.API_EditarMaster);
    return this.http
      .put<ApiResponse<any>>(url, masterEditada.toJson(), this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<ApiResponse<any>>(error, 'Erro ao editar a rede')
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(response.mensagens || ['Erro ao editar a rede']);
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  // ==================== Usuários ====================

  buscarListaUsuario(payload: {
    codEntidade: string;
    nome: string;
  }): Observable<UsuarioMaster[]> {
    const url = this.buildUrl(CONFIG_URLS_API.API_BuscarListaUsuario);
    return this.http
      .post<ApiResponse<UsuarioMaster[]>>(url, payload, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<UsuarioMaster[]>(
            error,
            'Erro ao carregar os usuários'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao carregar os usuários']
              );
            }
            return [];
          }
          return (response.retorno || []).map((item: any) =>
            UsuarioMaster.fromJson(item)
          );
        })
      );
  }

  criarNovoUsuario(
    novoUsuario: Partial<UsuarioMaster>
  ): Observable<ApiResponse<any>> {
    const url = this.buildUrl(CONFIG_URLS_API.API_CriarUsuarioMaster);
    return this.http
      .post<ApiResponse<any>>(url, novoUsuario, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<ApiResponse<any>>(
            error,
            'Erro ao criar o usuário'
          )
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(response.mensagens || ['Erro ao criar o usuário']);
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  atualizarUsuarioMaster(
    usuarioEditado: Partial<UsuarioMaster>
  ): Observable<ApiResponse<any>> {
    const url = this.buildUrl(CONFIG_URLS_API.API_EditarUsuarioMaster);
    return this.http
      .put<ApiResponse<any>>(url, usuarioEditado, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<ApiResponse<any>>(
            error,
            'Erro ao atualizar o usuário'
          )
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(
              response.mensagens || ['Erro ao atualizar o usuário']
            );
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  desativarLogin(
    payload: Partial<UsuarioMaster>
  ): Observable<ApiResponse<any>> {
    const url = this.buildUrl(CONFIG_URLS_API.API_DesativarUsuarioMaster);
    return this.http
      .put<ApiResponse<any>>(url, payload, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<ApiResponse<any>>(
            error,
            'Erro ao desativar o usuário'
          )
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(
              response.mensagens || ['Erro ao desativar o usuário']
            );
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  // ==================== Postos ====================

  buscarListaPostosAsync(master: AdmRedePosto): Observable<PostosMaster[]> {
    const url = this.buildUrl(CONFIG_URLS_API.API_BuscarListaPostosAdmRede);
    return this.http
      .post<ApiResponse<PostosMaster[]>>(
        url,
        master.toJson(),
        this.getHeaders()
      )
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<PostosMaster[]>(
            error,
            'Erro ao carregar os postos da rede'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao carregar os postos da rede']
              );
            }
            return [];
          }
          return (response.retorno || []).map((item: any) =>
            PostosMaster.fromJson(item)
          );
        })
      );
  }

  salvarAlteracoesPosto(payload: {
    idAdmRedePosto: string;
    master: string;
    postos: Array<{ codPosto: number; postoAtrelado: boolean }>;
  }): Observable<ApiResponse<any>> {
    const url = this.buildUrl(CONFIG_URLS_API.API_AtualizarPostosAdmRede);
    return this.http
      .put<ApiResponse<any>>(url, payload, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<ApiResponse<any>>(
            error,
            'Erro ao salvar os postos da rede'
          )
        ),
        map((response) => {
          if (response && !response.sucesso) {
            this.showError(
              response.mensagens || ['Erro ao salvar os postos da rede']
            );
          }
          return (
            response ||
            ({
              sucesso: false,
              retorno: null,
              mensagens: [],
            } as ApiResponse<any>)
          );
        })
      );
  }

  // ==================== Perfis ====================

  buscarListaPerfis(idMaster: number): Observable<PerfilMaster[]> {
    const url = this.buildUrl(CONFIG_URLS_API.API_BuscarPerfilPorEmpresa);
    return this.http
      .get<ApiResponse<PerfilMaster[]>>(`${url}/${idMaster}`, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<PerfilMaster[]>(
            error,
            'Erro ao carregar os perfis'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao carregar os perfis']
              );
            }
            return [];
          }
          return (response.retorno || []).map((item: any) =>
            PerfilMaster.fromJson(item)
          );
        })
      );
  }

  // ==================== Logs ====================

  pegarListaLogs(): Observable<LogMasterPosto[]> {
    const url = this.buildUrl(CONFIG_URLS_API.API_BuscarListaLogs);
    return this.http
      .get<ApiResponse<LogMasterPosto[]>>(url, this.getHeaders())
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleHttpError<LogMasterPosto[]>(
            error,
            'Erro ao carregar os logs'
          )
        ),
        map((response) => {
          if (!response || !response.sucesso) {
            if (response && !response.sucesso) {
              this.showError(
                response.mensagens || ['Erro ao carregar os logs']
              );
            }
            return [];
          }
          return (response.retorno || []).map((item: any) =>
            LogMasterPosto.fromJson(item)
          );
        })
      );
  }

  // ==================== Utils ====================

  gerarNomeArquivo(prefixo: string): string {
    return FileNameGenerator.generate(prefixo);
  }
}
